import { Banner, Toggle, useToast } from "@design";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router";
import type { ConnectorResponse } from "@/api";
import {
  errorMessage,
  getConnectorDetailOptions,
  getConnectorDetailQueryKey,
  listConnectorsQueryKey,
  updateConnectorMutation,
} from "@/api";
import { EntityDetailPage } from "@/components/build/EntityDetailPage";
import { InlineNotice } from "@/components/InlineNotice";
import { ConnectorAccessTab } from "./ConnectorAccessTab";
import { ConnectorCredentialsCard } from "./ConnectorCredentialsCard";
import { ConnectorForm } from "./ConnectorForm";
import { ConnectorSignInCard } from "./ConnectorSignInCard";
import { ConnectorToolsTab } from "./ConnectorToolsTab";
import { IntegrationManagedNotice } from "./IntegrationManagedNotice";
import { SetupNotesCard } from "./SetupNotesCard";
import { useDeleteConnector } from "./useDeleteConnector";

const canEditRole = (role?: string | null) => role === "admin" || role === "editor";

function StatusNotices({ connector }: Readonly<{ connector: ConnectorResponse }>) {
  const { t } = useTranslation("connectors");
  return (
    <>
      {connector.isOrg && <IntegrationManagedNotice connector={connector} />}
      {!connector.isOrg && connector.transport === "builtin" && connector.integrationName && (
        <InlineNotice tone="info">{t("instance.uses", { integration: connector.integrationName })}</InlineNotice>
      )}
      {connector.disabledByAdmin && (
        <Banner
          variant="error"
          message={
            connector.disabledReason
              ? `${t("detail.disabledByAdmin")} ${t("detail.disabledReason", { reason: connector.disabledReason })}`
              : t("detail.disabledByAdmin")
          }
        />
      )}
      {!connector.disabledByAdmin && connector.lastError && (
        <Banner variant="warning" message={t("detail.lastError", { error: connector.lastError })} />
      )}
    </>
  );
}

function EnabledToggle({ connector }: Readonly<{ connector: ConnectorResponse }>) {
  const { t } = useTranslation("connectors");
  const toast = useToast();
  const queryClient = useQueryClient();
  const update = useMutation(updateConnectorMutation());
  const path = { connector_uuid: connector.uuid };
  const set = (isEnabled: boolean) =>
    update.mutate(
      { path, body: { isEnabled } },
      {
        onSuccess: async () => {
          await Promise.all([
            queryClient.invalidateQueries({ queryKey: getConnectorDetailQueryKey({ path }) }),
            queryClient.invalidateQueries({ queryKey: listConnectorsQueryKey() }),
          ]);
          toast.success(t(isEnabled ? "detail.turnedOn" : "detail.turnedOff", { name: connector.name }));
        },
        onError: (error) => toast.error(errorMessage(error, t("detail.toggleFailed"))),
      },
    );
  return (
    <Toggle
      label={t("detail.enabled")}
      checked={connector.isEnabled && !connector.disabledByAdmin}
      disabled={connector.disabledByAdmin || update.isPending}
      onChange={set}
    />
  );
}

/** Build > Connectors > detail: Overview (settings + credential), Tools, Access. */
export function ConnectorDetailPage() {
  const { t } = useTranslation("connectors");
  const navigate = useNavigate();
  const { uuid = "" } = useParams();
  const query = useQuery(getConnectorDetailOptions({ path: { connector_uuid: uuid } }));
  const { onDelete, dialog } = useDeleteConnector(() => navigate("/build/connectors"));
  const data = query.data?.data;
  const canEdit = canEditRole(data?.currentUserRole);
  // An integration's own connector is changed on its integration (the API refuses edits here); its tools are not.
  const canEditSettings = canEdit && !data?.isOrg;

  return (
    <EntityDetailPage
      listPath="/build/connectors"
      rootLabel={t("title")}
      name={data?.name ?? ""}
      isPending={query.isPending}
      isError={query.isError}
      notFoundLabel={t("detail.notFound")}
      tabs={[
        {
          value: "overview",
          label: t("tabs.overview"),
          content: data && (
            <div className="flex flex-col gap-4">
              <StatusNotices connector={data} />
              {data.authMode === "oauth" && <ConnectorSignInCard connector={data} />}
              <ConnectorCredentialsCard connector={data} />
              {/* Distinct keys: siblings sharing a key leave stale copies in the DOM after updates (MHS-936). */}
              <SetupNotesCard key={`notes-${data.updatedAt}`} connector={data} canEdit={canEdit} />
              {canEditSettings && <ConnectorForm key={`form-${data.updatedAt}`} connector={data} />}
            </div>
          ),
        },
        // An integration's connector uses the integration's own tools (no discovery to show).
        ...(data?.transport === "builtin"
          ? []
          : [{ value: "tools", label: t("tabs.tools"), content: data && <ConnectorToolsTab connector={data} /> }]),
        {
          value: "access",
          label: t("tabs.access"),
          content:
            data &&
            (data.isOrg ? <IntegrationManagedNotice connector={data} /> : <ConnectorAccessTab connector={data} />),
        },
      ]}
      canDelete={data?.currentUserRole === "admin" && !data.isOrg}
      onDelete={() => data && onDelete(data)}
      deleteDialog={dialog}
      extraActions={data && canEditSettings ? <EnabledToggle connector={data} /> : undefined}
    />
  );
}
